// Server API endpoint for file uploads
const apiBaseUrl = import.meta.env.STON_ACADEMY_API_URL || '';

type DeletionScheduleItem = {
  fileId: string;
  deleteAt: string; // ISO string
};

/**
 * Upload a blob to S3 via the backend server and get a public URL
 */
export async function uploadBlobAndGetUrl(params: {
  blob: Blob;
  path?: string;
  contentType?: string;
  lang?: string;
}): Promise<string> {
  try {
    const formData = new FormData();
    formData.append('file', params.blob, params.path || 'file');
    if (params.lang) {
      formData.append('lang', params.lang);
    }

    const response = await fetch(`${apiBaseUrl}/api/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Upload failed with status ${response.status}`
      );
    }

    const data = await response.json();

    if (!data.success || !data.url) {
      throw new Error('Invalid response from upload server');
    }

    // Schedule file deletion after 7 days
    if (data.fileKey) {
      scheduleFileDeletion(data.fileKey, 7);
    }

    return data.url;
  } catch (error) {
    console.error('S3 upload via server failed:', error);
    // Fallback to local blob URL for local development
    return URL.createObjectURL(params.blob);
  }
}

/**
 * Schedule file deletion after a specified number of days
 */
export function scheduleFileDeletion(fileId: string, delayDays: number = 7) {
  const deleteTime = new Date();
  deleteTime.setDate(deleteTime.getDate() + delayDays);

  const raw = localStorage.getItem('deletionSchedule');
  const deletionSchedule: DeletionScheduleItem[] = raw
    ? (JSON.parse(raw) as DeletionScheduleItem[])
    : [];

  deletionSchedule.push({
    fileId,
    deleteAt: deleteTime.toISOString(),
  });

  localStorage.setItem('deletionSchedule', JSON.stringify(deletionSchedule));
}

/**
 * Check and delete expired files via the backend server
 */
export async function cleanupExpiredFiles() {
  const raw = localStorage.getItem('deletionSchedule');
  const deletionSchedule: DeletionScheduleItem[] = raw
    ? (JSON.parse(raw) as DeletionScheduleItem[])
    : [];
  const now = new Date();

  const remainingItems: DeletionScheduleItem[] = [];

  for (const item of deletionSchedule) {
    if (new Date(item.deleteAt) <= now) {
      try {
        await deleteFileFromServer(item.fileId);
        // File deleted successfully, don't add to remaining
      } catch (error) {
        console.error('Failed to delete file:', error);
        // Keep the item for retry later
        remainingItems.push(item);
      }
    } else {
      // Not yet expired, keep it
      remainingItems.push(item);
    }
  }

  localStorage.setItem('deletionSchedule', JSON.stringify(remainingItems));
}

/**
 * Delete a file from S3 via the backend server
 */
async function deleteFileFromServer(fileKey: string) {
  const response = await fetch(`${apiBaseUrl}/api/delete`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fileKey }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Delete failed with status ${response.status}`
    );
  }
}
